import React from 'react';
import Skeleton from '../../../../../components/Skeleton';
import { AttachmentPage as AttachmentPageData, PdfAttachmentBlockData } from '../../../config';
import './attachment-page.scss';

/**
 * "A4 portrait" / "A3 landscape" / "297 x 420 mm" for a page size in points,
 * with a little tolerance for the rounding PDF producers apply.
 */
export const describePageSize = (page: AttachmentPageData): string => {
  const w = page.widthPt;
  const h = page.heightPt;
  const landscape = w > h;
  const short = Math.min(w, h);
  const long = Math.max(w, h);
  const near = (a: number, b: number) => Math.abs(a - b) < 4;
  const orientation = landscape ? 'landscape' : 'portrait';
  if (near(short, 595) && near(long, 842)) return `A4 ${orientation}`;
  if (near(short, 842) && near(long, 1191)) return `A3 ${orientation}`;
  if (near(short, 1191) && near(long, 1684)) return `A2 ${orientation}`;
  if (near(short, 420) && near(long, 595)) return `A5 ${orientation}`;
  if (near(short, 612) && near(long, 792)) return `Letter ${orientation}`;
  return `${Math.round(w / 2.83465)} x ${Math.round(h / 2.83465)} mm`;
};

interface AttachmentPageProps {
  data: PdfAttachmentBlockData;
  pageIndex: number;
  thumbUrl: string | null;
}

/**
 * One page of an attached PDF, as the editor previews it: the thumbnail
 * rasterised at upload, filling a frame that DocumentEditor has already given
 * the page's own shape (so the page strip, the scroll-to-page and the
 * thumbnails treat it like any other page) with a chip naming its real size.
 * No margins: the sheet is somebody else's finished page. The export merges
 * the PDF's own page in at its real size; nothing here is measured or
 * paginated.
 */
const AttachmentPage: React.FC<AttachmentPageProps> = ({ data, pageIndex, thumbUrl }) => {
  const page = data.pages[pageIndex];
  if (!page) return null;
  return (
    <div className="attachment-page__inner">
      <div className="attachment-page__sheet">
        {thumbUrl ? (
          <img src={thumbUrl} alt={`${data.filename} page ${pageIndex + 1}`} draggable={false} />
        ) : (
          <Skeleton width="100%" height="100%" borderRadius={0} animation="wave" />
        )}
      </div>
      <div className="attachment-page__chip">
        {data.title || data.filename} · page {pageIndex + 1} of {data.pages.length} · {describePageSize(page)}
      </div>
    </div>
  );
};

export default AttachmentPage;
